import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import {
  addKlientContact,
  deleteKlientContact,
  getKlientContacts,
  updateKlientContact,
  type KlientContact,
} from '../api/klienti';
import { useFeather } from '../shell/useFeather';
import { AffilmasterContactDirectory, type AffilmasterContactDirectoryItem } from '../affilmaster/AffilmasterContactDirectory';
import { confirmDialog } from '../shell/dialogs';
import { MailingGroupsField, saveMailingGroups, type MailingGroupsValue } from '../mailing/MailingGroupsField';
import type { MailingSpace } from '../api/mailing';

type ContactForm = { name: string; email: string; phone: string; position: string; vocative: string };
const EMPTY_CONTACT: ContactForm = { name: '', email: '', phone: '', position: '', vocative: '' };

export function KlientKontakty({ id, canManage, onChanged, mailingSpace }: { id: number; canManage: boolean; onChanged?: () => void; mailingSpace: MailingSpace | null }) {
  const [rows, setRows] = useState<KlientContact[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [form, setForm] = useState<ContactForm | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [groups, setGroups] = useState<MailingGroupsValue | null>(null);
  const [origEmail, setOrigEmail] = useState('');
  const [groupVersion, setGroupVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getKlientContacts(id)
      .then((d) => { if (!cancelled) { setRows(d); setError(null); } })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Načtení kontaktů selhalo'); });
    return () => { cancelled = true; };
  }, [id, reload]);

  useFeather([rows, form]);

  function openAdd() { setEditId(null); setFormErr(null); setGroups(null); setOrigEmail(''); setForm({ ...EMPTY_CONTACT }); }
  function openEdit(c: KlientContact) {
    setEditId(c.id); setFormErr(null); setGroups(null); setOrigEmail(c.email ?? '');
    setForm({ name: c.name, email: c.email ?? '', phone: c.phone ?? '', position: c.position ?? '', vocative: c.vocative ?? '' });
  }
  async function save() {
    if (!form) return;
    if (form.name.trim().length < 2) { setFormErr('Jméno musí mít aspoň 2 znaky'); return; }
    setBusy(true); setFormErr(null);
    try {
      const p = { name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null, position: form.position.trim() || null, vocative: form.vocative.trim() || null };
      if (editId == null) await addKlientContact(id, p);
      else await updateKlientContact(id, editId, p);
      if (mailingSpace) await saveMailingGroups(origEmail, p.email, groups, mailingSpace);
      setForm(null); setGroups(null); setGroupVersion((v) => v + 1); setReload((r) => r + 1); onChanged?.();
    } catch (e) { setFormErr(e instanceof ApiError ? e.message : 'Uložení selhalo'); }
    finally { setBusy(false); }
  }
  async function remove(c: KlientContact) {
    if (!(await confirmDialog({ title: 'Smazat kontakt?', message: `Kontakt „${c.name}“ se smaže.`, confirmLabel: 'Smazat', danger: true }))) return;
    try { await deleteKlientContact(id, c.id); setReload((r) => r + 1); onChanged?.(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Smazání selhalo'); }
  }

  if (error && !rows) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!rows) return <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>;

  const pocet = rows.length === 1 ? '1 kontaktní osoba' : rows.length >= 2 && rows.length <= 4 ? `${rows.length} kontaktní osoby` : `${rows.length} kontaktních osob`;

  return (
    <div className="prototype-contacts-view">
      <div className="prototype-panel-head">
        <div><h2>Kontakty</h2><p>{pocet} klienta</p></div>
        {canManage && (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={openAdd}><i data-feather="plus" style={{ width: 14, height: 14 }} /> Přidat osobu</button>
        )}
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {rows.length === 0 ? <div className="prototype-empty-contact">Zatím žádný kontakt.</div> : (
        <AffilmasterContactDirectory
          key={groupVersion}
          mailingSpace={mailingSpace}
          label="Kontaktní osoby klienta"
          contacts={rows.map((c) => ({ id: c.id, name: c.name, role: c.position || 'Kontaktní osoba', vocative: c.vocative, email: c.email, phone: c.phone }))}
          onEdit={canManage ? (contact: AffilmasterContactDirectoryItem) => { const src = rows.find((r) => r.id === contact.id); if (src) openEdit(src); } : undefined}
          onDelete={canManage ? (contact: AffilmasterContactDirectoryItem) => { const src = rows.find((r) => r.id === contact.id); if (src) void remove(src); } : undefined}
        />
      )}
      {form && (
        <div className="prototype-modal-backdrop" onClick={() => setForm(null)}>
          <div onClick={(e) => e.stopPropagation()} className="card prototype-modal-card" style={{ width: '100%', maxWidth: 520 }}>
            <div className="prototype-modal-head"><div><div className="prototype-eyebrow">KONTAKT</div><h2>{editId == null ? 'Přidat osobu' : 'Upravit osobu'}</h2></div><button type="button" className="prototype-modal-close" aria-label="Zavřít" onClick={() => setForm(null)}>×</button></div>
            <div className="card-body">
              {formErr && <div className="alert alert-danger py-2">{formErr}</div>}
              <div className="row g-2">
                <div className="col-md-7"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Jméno *</label>
                  <input className="form-control form-control-sm" autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="col-md-5"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Funkce</label>
                  <input className="form-control form-control-sm" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="např. marketing manager" /></div>
                <div className="col-md-7"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>E-mail</label>
                  <input type="email" className="form-control form-control-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="col-md-5"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Telefon</label>
                  <input className="form-control form-control-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="col-12"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Vokativ (5. pád)</label>
                  <input className="form-control form-control-sm" value={form.vocative} onChange={(e) => setForm({ ...form, vocative: e.target.value })} placeholder="např. Matěji, Veroniko, Jiří" /></div>
                {mailingSpace && <div className="col-12"><MailingGroupsField space={mailingSpace} email={origEmail} currentEmail={form.email} value={groups} onChange={setGroups} /></div>}
              </div>
            </div>
            <div className="card-footer d-flex" style={{ gap: 8 }}>
              <button type="button" className="btn btn-sm btn-success" disabled={busy} onClick={save}>{busy ? 'Ukládám…' : 'Uložit'}</button>
              <button type="button" className="btn btn-sm btn-link ms-auto" onClick={() => setForm(null)}>Zrušit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
